import type { AxiosError } from 'axios';
import { isRedirectableURL } from 'libs/url';
import { organizationBillAddresses } from 'locales/ja/organization_bill_addresses';
import type { NextRouter } from 'next/router';
import { toast } from 'react-toastify';
import { getAdminsMe } from 'apis/admins/me';
import type { ContractsResponse } from 'apis/contracts/contracts_response_type';
import { getContractsPendingFind } from 'apis/contracts/find';
import { getOrganizationAdminsMe } from 'apis/organization_admins/me';
import { getTocIsUnread } from 'apis/toc_unreads/is_unread';
import { contractsShowPagePath, contractsPage } from 'pages/contracts/pages';
import { homePage } from 'pages/home/pages';
import { organizationBillAddressesNewPage } from 'pages/organization_bill_addresses/pages';
import { signInOrganizationsPage } from 'pages/pages';
import { usageNotesForAdminsPage } from 'pages/usage-notes/pages';
import { checkAuthorities } from 'utils/authorities';
import { httpErrorHandler } from 'utils/httpError';
import { checkRegistrationBillAddressRequired } from 'utils/organization_bill_addresses';

export const checkAdminsMe = (
  router: NextRouter,
  redirectUrl: string
): boolean => {
  // NOTE: セッションチェックのみで、情報は main-layout コンポーネント読み込み時に取得
  getAdminsMe()
    .then((response) => {
      if (response.status === 200)
        checkOrganizationAdminsMe(router, redirectUrl);
    })
    .catch((error: AxiosError) => {
      if (error.response?.status !== 401) httpErrorHandler(router, error);

      return false;
    });

  return true;
};

export const checkOrganizationAdminsMe = (
  router: NextRouter | null,
  redirectUrl: string
) => {
  if (!router?.isReady) return;

  // NOTE: セッションチェックのみで、情報は main-layout コンポーネント読み込み時に取得
  getOrganizationAdminsMe()
    .then((response) => {
      if (response.status === 200) redirectAfterPage(router, redirectUrl);
    })
    .catch((error: AxiosError) => {
      if (error.response?.status === 401) {
        router?.push({
          pathname: signInOrganizationsPage.path,
          query: redirectUrl ? { redirect_url: redirectUrl } : {},
        });
        return;
      }

      httpErrorHandler(router, error);
    });
};

const checkContractsUpdatable = async (): Promise<boolean> => {
  return await checkAuthorities('contracts', 'Update');
};

const checkOrganizationsAccessibilityUpdatable = async (): Promise<boolean> => {
  return await checkAuthorities('organizations', 'UpdateAccessibility');
};

const getPendingContracts = async (): Promise<Array<ContractsResponse>> => {
  const res = await getContractsPendingFind().catch(() => {
    return;
  });
  return Array.isArray(res?.data)
    ? (res?.data as Array<ContractsResponse>)
    : [];
};

const redirectSignedIn = (router: NextRouter, redirectUrl: string) => {
  if (redirectUrl && isRedirectableURL(redirectUrl)) {
    // リダイレクトURLがある場合、リダイレクトURLへ遷移
    window.location.assign(redirectUrl);
  } else if (redirectUrl && redirectUrl.startsWith('/')) {
    // リダイレクトURLにパスが指定されている場合、router で遷移
    router.push(redirectUrl);
  } else {
    // リダイレクトURLが無い場合、ホームへ遷移
    router.push(homePage.path);
  }
};

const redirectOrganizationBillAddressesNewPage = (
  router: NextRouter,
  redirectUrl: string
) => {
  checkRegistrationBillAddressRequired().then((required) => {
    if (required) {
      // 請求書送付先情報の登録が必要な場合
      toast.warn(
        `${organizationBillAddresses.resourceName}を登録してください`,
        {
          delay: 500,
        }
      );
      router.push(organizationBillAddressesNewPage.path);
    } else {
      redirectSignedIn(router, redirectUrl);
    }
  });
};

/* eslint-disable sonarjs/cognitive-complexity */
export const redirectAfterPage = (
  router: NextRouter | null,
  redirectUrl: string
) => {
  if (!router?.isReady) return;

  getTocIsUnread()
    .then((response) => {
      if (response.status === 200) {
        if (response.data.unread) {
          router.push({
            pathname: usageNotesForAdminsPage.path,
            query: redirectUrl ? { redirect_url: redirectUrl } : {},
          });
        } else {
          toast.success('サインインしました。', { delay: 500 });
          try {
            checkContractsUpdatable().then((contractsUpdatable) => {
              checkOrganizationsAccessibilityUpdatable().then(
                (organizationsAccessibilityUpdatable) => {
                  // 未承認契約の承認を、請求先情報登録より優先する
                  if (contractsUpdatable) {
                    getPendingContracts().then((pendingContracts) => {
                      if (pendingContracts.length === 0) {
                        // 契約の承認が不要の場合
                        if (organizationsAccessibilityUpdatable) {
                          // 請求書送付先情報の登録をチェックしてリダイレクト
                          redirectOrganizationBillAddressesNewPage(
                            router,
                            redirectUrl
                          );
                        } else {
                          // 組織のアクセシビリティ更新権限がない場合
                          redirectSignedIn(router, redirectUrl);
                        }
                      } else if (pendingContracts.length === 1) {
                        // 未承認の契約が１つの場合、契約確認ページへ遷移
                        router.push(
                          contractsShowPagePath(pendingContracts[0].id)
                        );
                      } else if (pendingContracts.length > 1) {
                        // 未承認の契約が２つ以上の場合、契約履歴ページへ遷移
                        router.push(contractsPage.path);
                      }
                    });
                  } else if (organizationsAccessibilityUpdatable) {
                    // 請求書送付先情報の登録をチェックしてリダイレクト
                    redirectOrganizationBillAddressesNewPage(
                      router,
                      redirectUrl
                    );
                  } else {
                    // 契約の更新権限、組織のアクセシビリティ更新権限がない場合
                    redirectSignedIn(router, redirectUrl);
                  }
                }
              );
            });
          } catch (_error) {
            // fail safe
            redirectSignedIn(router, redirectUrl);
          }
        }
      }
    })
    .catch((error: AxiosError) => {
      httpErrorHandler(router, error);
    });
};
/* eslint-enable sonarjs/cognitive-complexity */
